import { useRef, useState } from "react";
import confetti from "canvas-confetti";
import { RotateCcw } from "lucide-react";
import { ToothMascot } from "@/components/site/ToothMascot";

const makeSpots = () => Array.from({ length: 14 }, (_, k) => ({
  id: k, x: 18 + Math.random() * 64, y: 18 + Math.random() * 62, r: 5 + Math.random() * 4, life: 3,
}));

const TIPS = [
  "Hold your brush at a slant towards the gums.",
  "Use small gentle circles - no hard scrubbing!",
  "Don't forget the inside and the chewing tops.",
  "Spit out the toothpaste, but don't rinse - fluoride keeps working.",
];

export const ScrubTooth = () => {
  const [spots, setSpots] = useState(makeSpots);
  const [tip, setTip] = useState(0);
  const box = useRef(null);
  const left = spots.filter((s) => s.life > 0).length;

  const scrub = (e) => {
    if (e.buttons === 0 && e.pointerType === "mouse") return;
    const r = box.current.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * 100;
    const py = ((e.clientY - r.top) / r.height) * 100;
    setSpots((ss) => {
      const next = ss.map((s) => (s.life > 0 && Math.hypot(s.x - px, s.y - py) < s.r + 4 ? { ...s, life: s.life - 1 } : s));
      const before = ss.filter((s) => s.life > 0).length;
      const after = next.filter((s) => s.life > 0).length;
      if (after < before) setTip((t) => (t + 1) % TIPS.length);
      if (before > 0 && after === 0) confetti({ particleCount: 150, spread: 100 });
      return next;
    });
  };

  return (
    <div className="rounded-[2.5rem] bg-[#fef7e5] p-6 sm:p-10" data-testid="game-scrub-tooth">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold sm:text-3xl">Scrub-a-Tooth</h2>
        <span className="rounded-full bg-white px-3 py-1.5 text-sm font-bold" data-testid="scrub-left">Plaque left: {left}</span>
      </div>
      <p className="mt-2 text-sm font-semibold text-slate-600">Press and rub over the yellow plaque spots to make Sparkle shine.</p>
      <div ref={box} onPointerMove={scrub} onPointerDown={scrub} data-testid="scrub-area"
        className="relative mx-auto mt-6 aspect-square w-full max-w-xs touch-none cursor-grab select-none active:cursor-grabbing">
        <ToothMascot mood={left === 0 ? "wow" : "happy"} className="h-full w-full" />
        {spots.map((s) => s.life > 0 && (
          <span key={s.id} className="pointer-events-none absolute rounded-full bg-[#d4a017] transition-opacity"
            style={{ left: `${s.x - s.r}%`, top: `${s.y - s.r}%`, width: `${s.r * 2}%`, height: `${s.r * 2}%`, opacity: 0.25 + s.life * 0.22 }} />
        ))}
      </div>
      <p className="font-display mt-4 text-center text-lg font-semibold text-teal-700" data-testid="scrub-tip">
        {left === 0 ? "Squeaky clean! That's how plaque leaves when you brush well." : `Brushing tip: ${TIPS[tip]}`}
      </p>
      <div className="mt-4 text-center">
        <button onClick={() => setSpots(makeSpots())} data-testid="scrub-reset"
          className="btn-pop inline-flex items-center gap-2 rounded-full border-2 border-slate-800 bg-white px-5 py-2.5 text-sm font-bold text-slate-800"><RotateCcw className="h-4 w-4" />New dirty tooth</button>
      </div>
    </div>
  );
};
